import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Head, Loader } from "./shared";
import { MissionTimelineDrawer } from "@/pages/studio/AIIntelligence";
import {
  PenLine, Search, FileText, Newspaper, HelpCircle, MousePointerClick, Sparkle, Palette, Network,
  Sparkles, Loader2, ChevronDown, Copy, Wand2, Maximize2, Minimize2, RefreshCw, PlusCircle,
  Save, FileDown, Send, History, Bot, Cpu, RotateCcw, Eye, CheckCircle2, UploadCloud, Activity, Trash2, Brain, Rocket, Briefcase,
} from "lucide-react";
import { TypeDeleteDialog } from "@/components/TypeDeleteDialog";
import { WebsiteBuilder } from "./WebsiteBuilder";
import { BusinessPlanBuilder } from "./BusinessPlanBuilder";
import { AiSwitch } from "@/components/AiSwitch";

const WB_ID = "__website_builder__";
const BP_ID = "__business_plan__";

const ICONS = { PenLine, Search, FileText, Newspaper, HelpCircle, MousePointerClick, Sparkle, Palette, Network };
const REFINE = [
  ["improve", "Improve", Wand2], ["expand", "Expand", Maximize2], ["shorten", "Shorten", Minimize2],
  ["rewrite", "Rewrite", RefreshCw], ["regenerate", "Regenerate", PlusCircle],
];

export function AICenter() {
  const [config, setConfig] = useState(null);
  const [genId, setGenId] = useState(null);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  // Client/Project selection lives here (parent) so the "Digital Brain" persists
  // as the operator moves between generators — one unified profile, not per-tool.
  const [clientId, setClientId] = useState("");
  const [projectId, setProjectId] = useState("");

  useEffect(() => {
    api.get("/ai/studio/config").then((r) => {
      setConfig(r.data);
      setGenId(WB_ID);
    }).catch(() => setConfig({ generators: [] }));
    reloadClients();
    reloadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reloadClients = () => api.get("/admin/clients").then((r) => { setClients(r.data); return r.data; }).catch(() => []);
  const reloadProjects = () => api.get("/projects").then((r) => { setProjects(r.data); return r.data; }).catch(() => []);

  const gen = useMemo(() => config?.generators.find((g) => g.id === genId), [config, genId]);
  if (!config) return <Loader />;

  return (
    <div>
      <Head title="AI Content Studio" desc="Generate, refine, version, export and route professional business content — powered by AIPOS." />
      <AiSwitch />
      <div className={`flex items-center gap-2 text-xs px-3 py-2 mb-4 border ${config.openrouter_active ? "text-green-300 bg-green-500/10 border-green-500/30" : "text-gold/90 bg-gold/10 border-gold/30"}`} data-testid="ai-gateway-status">
        <Cpu size={14} />
        {config.openrouter_active ? (
          <span>AI Provider: <b className="text-green-300">OpenRouter</b> — active. AIPOS routes each request to the best model (auto-fallback to Emergent).</span>
        ) : (
          <span>AI Gateway: <b className="text-gold">Emergent (fallback)</b> active. Add your OpenRouter key to switch the whole studio to the OpenRouter gateway — no other changes needed.</span>
        )}
      </div>
      <div className="grid lg:grid-cols-[240px_1fr] gap-5">
        {/* Generator picker */}
        <div className="space-y-1.5" data-testid="ai-generator-list">
          <button onClick={() => setGenId(WB_ID)} data-testid="ai-gen-website-builder"
            className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left text-sm border transition-colors ${genId === WB_ID ? "bg-gold/10 border-gold/50 text-white" : "bg-navy-800 border-white/10 text-gray-400 hover:text-white hover:border-white/25"}`}>
            <Rocket size={16} className={genId === WB_ID ? "text-gold" : ""} /> <span className="truncate">Website Content Builder</span>
          </button>
          <button onClick={() => setGenId(BP_ID)} data-testid="ai-gen-business-plan"
            className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left text-sm border transition-colors ${genId === BP_ID ? "bg-gold/10 border-gold/50 text-white" : "bg-navy-800 border-white/10 text-gray-400 hover:text-white hover:border-white/25"}`}>
            <Briefcase size={16} className={genId === BP_ID ? "text-gold" : ""} /> <span className="truncate">Business Plan Builder</span>
          </button>
          <div className="text-[10px] uppercase tracking-wider text-gray-600 px-3 pt-2 pb-1">Single generators</div>
          {config.generators.map((g) => {
            const Icon = ICONS[g.icon] || Bot;
            return (
              <button key={g.id} onClick={() => setGenId(g.id)} data-testid={`ai-gen-${g.id}`}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left text-sm border transition-colors ${genId === g.id ? "bg-gold/10 border-gold/50 text-white" : "bg-navy-800 border-white/10 text-gray-400 hover:text-white hover:border-white/25"}`}>
                <Icon size={16} className={genId === g.id ? "text-gold" : ""} /> <span className="truncate">{g.label.replace(" Generator", "").replace(" Assistant", "").replace(" Builder", "").replace(" Writer", "")}</span>
              </button>
            );
          })}
        </div>
        {genId === WB_ID
          ? <WebsiteBuilder config={config} clients={clients} projects={projects}
              clientId={clientId} setClientId={setClientId} projectId={projectId} setProjectId={setProjectId}
              reloadClients={reloadClients} reloadProjects={reloadProjects} />
          : genId === BP_ID
          ? <BusinessPlanBuilder config={config} clients={clients} projects={projects}
              clientId={clientId} setClientId={setClientId} projectId={projectId} setProjectId={setProjectId}
              reloadClients={reloadClients} reloadProjects={reloadProjects} />
          : gen && <Workspace key={gen.id} gen={gen} config={config} clients={clients} projects={projects}
          clientId={clientId} setClientId={setClientId} projectId={projectId} setProjectId={setProjectId} />}
      </div>
    </div>
  );
}

const SEL = "w-full bg-navy border border-white/20 text-white p-2.5 text-sm focus:border-gold focus:outline-none";
const LBL = "block text-xs uppercase tracking-wide text-gray-500 mb-1.5";

// Provenance chip — shows "where this information came from" next to auto-filled fields.
const Prov = ({ src }) => src ? (
  <span data-testid="bi-provenance" className="ml-2 inline-flex items-center gap-1 text-[10px] normal-case tracking-normal text-emerald-300/90 align-middle">
    <CheckCircle2 size={10} /> {src}
  </span>
) : null;

function SmartSelect({ label, hint, value, custom, onValue, onCustom, options, testid, customPrompt }) {
  const isOther = value === "Other (Custom)";
  return (
    <div>
      <label className={LBL}>{label}{hint}</label>
      <select className={SEL} value={value} onChange={(e) => onValue(e.target.value)} data-testid={testid}>
        <option value="">Select…</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
      {isOther && <input className={`${SEL} mt-2`} placeholder={customPrompt} value={custom} onChange={(e) => onCustom(e.target.value)} data-testid={`${testid}-custom`} />}
    </div>
  );
}

function Workspace({ gen, config, clients, projects, clientId, setClientId, projectId, setProjectId }) {
  const [f, setF] = useState({
    output_type: gen.output_types[0]?.id || "", length: gen.lengths[0] || "standard", custom_words: 800,
    industry: "", industry_custom: "", goal: "", goal_custom: "", audience: "", audience_custom: "",
    tone: "Professional", tone_custom: "", model: "auto", context: "", advanced: {},
  });
  const [showAdv, setShowAdv] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refining, setRefining] = useState("");
  const [asset, setAsset] = useState(null); // {asset_id, version, result, title, provider, model}
  const [versions, setVersions] = useState([]);
  const [bi, setBi] = useState(null);
  const [biLoading, setBiLoading] = useState(false);
  const [showMission, setShowMission] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const resultRef = useRef(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setAdv = (k, v) => setF((p) => ({ ...p, advanced: { ...p.advanced, [k]: v } }));

  const val = (base) => (f[base] === "Other (Custom)" ? f[`${base}_custom`] : f[base]);

  // Business Intelligence read model: when a Client/Project is selected, auto-load
  // what AIPOS already knows — tailored to THIS generator — and pre-fill fields as
  // editable overrides. Every generator "begins by reading what we already know."
  const applyProfile = (d) => {
    const fld = d.fields || {};
    const smart = (base, options, value) => {
      if (!value) return;
      setF((p) => (options.includes(value)
        ? { ...p, [base]: value, [`${base}_custom`]: "" }
        : { ...p, [base]: "Other (Custom)", [`${base}_custom`]: value }));
    };
    if (fld.context?.value) set("context", fld.context.value);
    smart("industry", config.industries, fld.industry?.value);
    smart("goal", config.goals, fld.goal?.value);
    smart("audience", config.audiences, fld.audience?.value);
    const adv = fld.advanced || {};
    const advVals = Object.fromEntries(Object.entries(adv).map(([k, v]) => [k, v?.value]).filter(([, v]) => v));
    if (Object.keys(advVals).length) setF((p) => ({ ...p, advanced: { ...p.advanced, ...advVals } }));
  };
  useEffect(() => {
    if (!clientId && !projectId) { setBi(null); return; }
    setBiLoading(true);
    api.get("/ai/studio/business-intelligence", { params: { client_id: clientId || undefined, project_id: projectId || undefined, generator: gen.id } })
      .then((r) => { setBi(r.data); if (r.data?.loaded) applyProfile(r.data); })
      .catch(() => setBi(null))
      .finally(() => setBiLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, projectId]);

  const generate = async () => {
    if (!f.context.trim()) { toast.error("Add some business context first"); return; }
    setBusy(true); setAsset(null); setVersions([]);
    try {
      const { data } = await api.post("/ai/studio/generate", {
        generator: gen.id, output_type: f.output_type, length: f.length,
        custom_words: f.length === "custom" ? Number(f.custom_words) : null,
        industry: val("industry"), goal: val("goal"), audience: val("audience"), tone: val("tone"),
        model: f.model, advanced: f.advanced, context: f.context,
        client_id: clientId || null, project_id: projectId || null,
      });
      setAsset(data);
      toast.success(`Generated via ${data.provider}${data.exec_ms ? ` · ${(data.exec_ms / 1000).toFixed(1)}s` : ""}`);
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const refine = async (action) => {
    if (!asset) return;
    setRefining(action);
    try {
      const { data } = await api.post("/ai/studio/refine", { asset_id: asset.asset_id, action, model: f.model });
      setAsset((a) => ({ ...a, result: data.result, version: data.version, provider: data.provider, model: data.model,
        tokens: data.tokens ?? a.tokens, cost_estimated: data.cost_estimated ?? a.cost_estimated, cost_source: data.cost_source ?? a.cost_source }));
      toast.success(`${action.charAt(0).toUpperCase() + action.slice(1)} → v${data.version}`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setRefining(""); }
  };

  const loadVersions = async () => {
    if (!asset) return;
    const { data } = await api.get(`/ai/studio/assets/${asset.asset_id}`);
    setVersions(data.versions || []);
  };
  const rollback = async (v) => {
    try {
      const { data } = await api.post(`/ai/studio/assets/${asset.asset_id}/rollback/${v}`);
      setAsset((a) => ({ ...a, result: data.result, version: data.version }));
      toast.success(`Rolled back to v${v} (saved as v${data.version})`); loadVersions();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const save = async () => {
    if (!asset) return;
    if (!clientId && !projectId) { toast.error("Pick a client or project to save to"); return; }
    try {
      await api.post("/ai/studio/save", { asset_id: asset.asset_id, client_id: clientId || null, project_id: projectId || null });
      toast.success("Saved to " + [projectId && "project", clientId && "client"].filter(Boolean).join(" & "));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const route = async (destination) => {
    if (!asset) return;
    try {
      const { data } = await api.post("/ai/studio/route", { asset_id: asset.asset_id, destination });
      toast.success(data.message);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const exportDoc = async (kind) => {
    if (!asset) return;
    try {
      const res = await api.post(`/ai/studio/export/${kind}`, { asset_id: asset.asset_id }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `${asset.title.replace(/[^a-z0-9]+/gi, "-")}.${kind}`; a.click(); URL.revokeObjectURL(url);
      toast.success(`Exported ${kind.toUpperCase()}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(asset.result); toast.success("Copied"); }
    catch { const t = resultRef.current?.querySelector("textarea"); if (t) { t.select(); document.execCommand("copy"); toast.success("Copied"); } else toast.error("Copy failed — select the text manually"); }
  };

  const lifecycle = async (stage) => {
    if (!asset) return;
    try {
      const { data } = await api.post(`/ai/studio/assets/${asset.asset_id}/${stage}`);
      setAsset((a) => ({
        ...a, review_status: data.review_status,
        ...(stage === "approve" ? { confidence_score: 1.0, confidence_source: "Human Confidence" } : {}),
      }));
      toast.success(`Marked ${data.review_status}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteAsset = async () => {
    if (!asset) return;
    try {
      await api.post(`/ai/studio/assets/${asset.asset_id}/delete`, { confirm: "DELETE" });
      toast.success("Asset permanently deleted.");
      setDelOpen(false); setAsset(null); setVersions([]);
    } catch (e) { toast.error(formatApiError(e)); throw e; }
  };

  return (
    <div className="space-y-5">
      {/* BUSINESS INTELLIGENCE — auto-loaded knowledge with provenance */}
      {bi?.loaded && (
        <div className="bg-navy-800 border border-gold/20 p-4" data-testid="bi-panel">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-white font-medium">
              <Brain size={16} className="text-gold" /> Business Intelligence
              {bi.company?.org_name && <span className="text-gray-400 text-sm">· {bi.company.org_name}</span>}
            </div>
            <div className="flex items-center gap-2" data-testid="bi-knowledge-score" title="Business DNA — how much AIPOS knows about this business">
              <span className="text-[11px] uppercase tracking-wide text-gray-500">Knowledge</span>
              <div className="w-28 h-1.5 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-gold transition-all" style={{ width: `${bi.knowledge.score}%` }} />
              </div>
              <span className="text-gold text-sm font-heading">{bi.knowledge.score}%</span>
            </div>
          </div>
          <p className="text-gray-500 text-xs mt-1">Auto-loaded from what AIPOS already knows — every field below is an editable override.</p>
          <div className="flex flex-wrap gap-2 mt-3">
            {bi.knowledge.dimensions.map((d) => (
              <span key={d.key} data-testid={`bi-dim-${d.key}`}
                className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-1 border rounded-full ${d.filled ? "border-emerald-500/30 text-emerald-200 bg-emerald-500/[0.06]" : "border-white/10 text-gray-500"}`}>
                {d.filled ? <CheckCircle2 size={11} className="text-emerald-400" /> : <span className="w-1.5 h-1.5 rounded-full bg-gray-600" />}
                {d.label}{d.source && <span className="text-gray-500">· {d.source}</span>}
              </span>
            ))}
          </div>
        </div>
      )}
      {/* CONFIG */}
      <div className="bg-navy-800 border border-white/10 p-5 space-y-4" data-testid="ai-workspace">
        <div className="flex items-center gap-2 text-white font-medium">{(() => { const I = ICONS[gen.icon] || Bot; return <I size={18} className="text-gold" />; })()} {gen.label}</div>
        {/* Client / Project context */}
        <div className="grid md:grid-cols-2 gap-4">
          <div><label className={LBL}>Client (optional)</label>
            <select className={SEL} value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="ai-client">
              <option value="">— none —</option>
              {clients.map((c) => <option key={c.user_id} value={c.user_id}>{c.name || c.email}</option>)}
            </select></div>
          <div><label className={LBL}>Project (optional)</label>
            <select className={SEL} value={projectId} onChange={(e) => setProjectId(e.target.value)} data-testid="ai-project">
              <option value="">— none —</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.org_name}</option>)}
            </select></div>
        </div>
        <div><label className={LBL}>Business context<Prov src={bi?.fields?.context?.source} /></label>
          <textarea rows={3} className={SEL} value={f.context} onChange={(e) => set("context", e.target.value)} data-testid="ai-context"
            placeholder="Describe the business, offerings, differentiators, key details…" /></div>
        <div className="grid md:grid-cols-3 gap-4">
          <div><label className={LBL}>Output type</label>
            <select className={SEL} value={f.output_type} onChange={(e) => set("output_type", e.target.value)} data-testid="ai-output-type">
              {gen.output_types.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select></div>
          <div><label className={LBL}>Length</label>
            <select className={SEL} value={f.length} onChange={(e) => set("length", e.target.value)} data-testid="ai-length">
              {gen.lengths.map((l) => { const m = config.lengths.find((x) => x.id === l); return <option key={l} value={l}>{m?.label || l}</option>; })}
            </select>
            {f.length === "custom" && <input type="number" className={`${SEL} mt-2`} value={f.custom_words} onChange={(e) => set("custom_words", e.target.value)} placeholder="Word count" data-testid="ai-custom-words" />}
          </div>
          <div><label className={LBL}>AI model</label>
            <select className={SEL} value={f.model} onChange={(e) => set("model", e.target.value)} data-testid="ai-model">
              {config.models.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select></div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <SmartSelect label="Industry" hint={<Prov src={bi?.fields?.industry?.source} />} value={f.industry} custom={f.industry_custom} onValue={(v) => set("industry", v)} onCustom={(v) => set("industry_custom", v)} options={config.industries} testid="ai-industry" customPrompt="What industry are you in?" />
          <SmartSelect label="Business goal" hint={<Prov src={bi?.fields?.goal?.source} />} value={f.goal} custom={f.goal_custom} onValue={(v) => set("goal", v)} onCustom={(v) => set("goal_custom", v)} options={config.goals} testid="ai-goal" customPrompt="What is your primary business goal?" />
          <SmartSelect label="Target audience" hint={<Prov src={bi?.fields?.audience?.source} />} value={f.audience} custom={f.audience_custom} onValue={(v) => set("audience", v)} onCustom={(v) => set("audience_custom", v)} options={config.audiences} testid="ai-audience" customPrompt="Describe your ideal customer." />
          <SmartSelect label="Tone" value={f.tone} custom={f.tone_custom} onValue={(v) => set("tone", v)} onCustom={(v) => set("tone_custom", v)} options={config.tones} testid="ai-tone" customPrompt="Describe the tone you want." />
        </div>

        {/* Advanced */}
        <div className="border border-white/10">
          <button onClick={() => setShowAdv((s) => !s)} data-testid="ai-advanced-toggle" className="w-full flex items-center justify-between px-3 py-2.5 text-sm text-white hover:bg-white/5">
            <span className="flex items-center gap-2"><Cpu size={14} className="text-gold" /> Advanced options</span>
            <ChevronDown size={15} className={`transition-transform ${showAdv ? "rotate-180" : ""}`} />
          </button>
          {showAdv && (
            <div className="px-3 pb-4 pt-1 grid md:grid-cols-3 gap-3" data-testid="ai-advanced">
              {config.advanced_options.map((o) => (
                <div key={o.id}>
                  {o.type === "bool" ? (
                    <label className="flex items-center gap-2 text-xs text-gray-300 mt-5 cursor-pointer">
                      <input type="checkbox" className="accent-gold" checked={!!f.advanced[o.id]} onChange={(e) => setAdv(o.id, e.target.checked)} data-testid={`ai-adv-${o.id}`} /> {o.label}
                    </label>
                  ) : o.type === "select" ? (
                    <><label className={LBL}>{o.label}<Prov src={bi?.fields?.advanced?.[o.id]?.source} /></label>
                      <select className={SEL} value={f.advanced[o.id] || ""} onChange={(e) => setAdv(o.id, e.target.value)} data-testid={`ai-adv-${o.id}`}>
                        <option value="">Default</option>{o.options.map((x) => <option key={x} value={x}>{x}</option>)}
                      </select></>
                  ) : (
                    <><label className={LBL}>{o.label}<Prov src={bi?.fields?.advanced?.[o.id]?.source} /></label>
                      <input className={SEL} value={f.advanced[o.id] || ""} onChange={(e) => setAdv(o.id, e.target.value)} data-testid={`ai-adv-${o.id}`} /></>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <button onClick={generate} disabled={busy} data-testid="ai-generate-btn"
          className="w-full flex items-center justify-center gap-2 bg-gold text-navy py-3 font-semibold hover:bg-gold-light transition-colors disabled:opacity-60">
          {busy ? <Loader2 className="animate-spin" size={17} /> : <Sparkles size={17} />} {busy ? "Generating…" : "Generate"}
        </button>
      </div>

      {/* RESULTS */}
      {asset && (
        <div ref={resultRef} className="bg-navy-800 border border-gold/30 p-5 space-y-4" data-testid="ai-result">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-white font-medium">{asset.title}</div>
            <div className="flex items-center gap-2 text-xs text-gray-400 flex-wrap">
              <span className="px-2 py-0.5 border border-white/15 rounded-full" data-testid="ai-result-version">v{asset.version}</span>
              <span className="px-2 py-0.5 border border-white/15 rounded-full">{asset.provider} · {asset.model?.split("/").pop()}</span>
              {asset.tokens != null && <span className="px-2 py-0.5 border border-white/15 rounded-full" data-testid="ai-result-tokens">{asset.tokens} tok</span>}
              {asset.cost_estimated != null && <span className="px-2 py-0.5 border border-gold/30 text-gold rounded-full" data-testid="ai-result-cost">${Number(asset.cost_estimated).toFixed(4)} <span className="text-gray-500">{asset.cost_source || "est"}</span></span>}
              {asset.confidence_score != null && <span className="px-2 py-0.5 border border-white/15 rounded-full" data-testid="ai-result-confidence" title={asset.confidence_source}>{Math.round(asset.confidence_score * 100)}% conf</span>}
              {asset.review_status && <span className="px-2 py-0.5 border border-emerald-500/30 text-emerald-300 rounded-full capitalize" data-testid="ai-result-review-status">{asset.review_status}</span>}
            </div>
          </div>
          {asset.confidence_source && (
            <div className="text-[11px] text-gray-500" data-testid="ai-confidence-source">Confidence source: {asset.confidence_source}</div>
          )}

          {/* AIPOS Router Confidence Indicator — shown when Auto selected the model */}
          {asset.router_recommendation && (
            <div className="border border-gold/30 bg-gold/[0.06] rounded-md p-3.5 flex items-start gap-3" data-testid="ai-router-recommendation">
              <div className="w-8 h-8 rounded-md bg-gold/15 flex items-center justify-center shrink-0 mt-0.5">
                <Bot size={16} className="text-gold" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] uppercase tracking-wide text-gold font-semibold">AIPOS Recommendation</span>
                  {(() => {
                    const c = asset.router_recommendation.confidence || "Low";
                    const tone = c === "High" ? "text-emerald-300 border-emerald-500/40" : c === "Medium" ? "text-amber-300 border-amber-500/40" : "text-gray-400 border-white/15";
                    return <span className={`text-[10px] px-2 py-0.5 border rounded-full ${tone}`} data-testid="ai-router-confidence">Confidence: {c}</span>;
                  })()}
                  {asset.router_recommendation.learning_active
                    ? <span className="text-[10px] px-2 py-0.5 border border-emerald-500/30 text-emerald-300 rounded-full">Learned</span>
                    : <span className="text-[10px] px-2 py-0.5 border border-white/15 text-gray-400 rounded-full">Cold-start</span>}
                </div>
                <div className="text-white text-sm mt-1" data-testid="ai-router-model">Model: {asset.router_recommendation.label || asset.model}</div>
                <div className="text-[12px] text-gray-400 mt-0.5" data-testid="ai-router-reason">{asset.router_recommendation.reason}</div>
              </div>
            </div>
          )}
          <textarea value={asset.result} onChange={(e) => setAsset((a) => ({ ...a, result: e.target.value }))} rows={14}
            data-testid="ai-result-text" className="w-full bg-navy border border-white/15 text-gray-100 p-4 text-sm leading-relaxed focus:border-gold focus:outline-none font-mono" />

          {/* Refine */}
          <div className="flex flex-wrap gap-2">
            {REFINE.map(([act, lbl, Icon]) => (
              <button key={act} onClick={() => refine(act)} disabled={!!refining} data-testid={`ai-refine-${act}`}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-white/15 text-gray-200 hover:border-gold/50 hover:text-white transition-colors disabled:opacity-50">
                {refining === act ? <Loader2 className="animate-spin" size={13} /> : <Icon size={13} />} {lbl}
              </button>
            ))}
          </div>

          {/* Actions */}
          <div className="border-t border-white/10 pt-4 flex flex-wrap gap-2">
            <Act icon={Copy} label="Copy" onClick={copy} testid="ai-copy" />
            <Act icon={Save} label="Save to Client/Project" onClick={save} testid="ai-save" gold />
            <Act icon={FileDown} label="Word" onClick={() => exportDoc("docx")} testid="ai-export-docx" />
            <Act icon={FileDown} label="PDF" onClick={() => exportDoc("pdf")} testid="ai-export-pdf" />
            {gen.destinations?.includes("proposal") && <Act icon={Send} label="Send to Proposal" onClick={() => route("proposal")} testid="ai-route-proposal" />}
            {gen.destinations?.includes("website") && <Act icon={Send} label="Send to Website Builder" onClick={() => route("website")} testid="ai-route-website" />}
            <Act icon={History} label="History" onClick={() => { loadVersions(); }} testid="ai-history" />
          </div>

          {/* Human review lifecycle: AI Generated → Reviewed → Approved → Published */}
          <div className="border-t border-white/10 pt-4 flex flex-wrap items-center gap-2" data-testid="ai-review-lifecycle">
            <span className="text-[11px] uppercase tracking-wide text-gray-500 mr-1">Review Lifecycle</span>
            <Act icon={Eye} label="Mark Reviewed" onClick={() => lifecycle("review")} testid="ai-review" />
            <Act icon={CheckCircle2} label="Approve" onClick={() => lifecycle("approve")} testid="ai-approve" gold />
            <Act icon={UploadCloud} label="Publish" onClick={() => lifecycle("publish")} testid="ai-publish" />
            <Act icon={Activity} label="View Mission Timeline" onClick={() => setShowMission(true)} testid="ai-view-mission-timeline" />
            <button onClick={() => setDelOpen(true)} data-testid="ai-delete-asset"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-red-500/30 text-red-300 hover:bg-red-500/10 transition-colors ml-auto">
              <Trash2 size={13} /> Delete Permanently
            </button>
          </div>

          {versions.length > 0 && (
            <div className="border-t border-white/10 pt-3 space-y-1.5" data-testid="ai-version-history">
              <div className="text-xs uppercase tracking-wide text-gray-500">Version history</div>
              {versions.slice().reverse().map((v) => (
                <div key={v.v} className="flex items-center gap-3 text-xs text-gray-400 bg-navy/40 border border-white/5 px-3 py-2">
                  <span className="text-white font-medium">v{v.v}</span>
                  <span className="capitalize">{v.action}</span>
                  <span className="text-gray-600">{v.model?.split("/").pop() || v.provider}</span>
                  <span className="flex-1" />
                  <span className="text-gray-600">{new Date(v.created_at).toLocaleString()}</span>
                  <button onClick={() => rollback(v.v)} data-testid={`ai-rollback-${v.v}`} className="flex items-center gap-1 text-gold hover:text-gold-light"><RotateCcw size={12} /> Restore</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {showMission && asset?.mission_id && (
        <MissionTimelineDrawer openId={asset.mission_id} onClose={() => setShowMission(false)} />
      )}
      <TypeDeleteDialog open={delOpen} count={1} label="AI asset" onConfirm={deleteAsset} onCancel={() => setDelOpen(false)} />
    </div>
  );
}

const Act = ({ icon: Icon, label, onClick, testid, gold }) => (
  <button onClick={onClick} data-testid={testid}
    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border transition-colors ${gold ? "border-gold/50 text-gold hover:bg-gold/10" : "border-white/15 text-gray-200 hover:border-gold/50 hover:text-white"}`}>
    <Icon size={13} /> {label}
  </button>
);
